<template>
  <header class="desktop-header">
    <Logo class="desktop-header__logo" />

    <nav class="desktop-header__settings">
      <!-- VIEW : one pane / split. The only control here that is desktop-only. -->
      <div class="desktop-header__view">
        <span class="desktop-header__label">{{ $t('View') }} :</span>
        <button
          type="button"
          class="desktop-header__view-btn"
          :class="{ active: shownViewMode === 1 }"
          :aria-pressed="String(shownViewMode === 1)"
          :aria-label="$t('Single pane')"
          @click="setViewMode(1)"
        >
          <img :src="shownViewMode === 1 ? icons.oneActive : icons.one" alt="" />
        </button>
        <button
          type="button"
          class="desktop-header__view-btn"
          :class="{ active: shownViewMode === 2 }"
          :aria-pressed="String(shownViewMode === 2)"
          :aria-label="$t('Split view')"
          :disabled="singleOnly"
          @click="setViewMode(2)"
        >
          <img :src="shownViewMode === 2 ? icons.twoActive : icons.two" alt="" />
        </button>
      </div>

      <!--
        MAX : the per-fight limit. The hover breakdown is the v1 header's, kept
        because it is the only place the draw limit is shown.
      -->
      <div class="desktop-header__max">
        <span class="desktop-header__label">{{ $t('Max') }} :</span>
        <span class="desktop-header__value">{{ formatAmountWithKM(mainLimitAmountPerFight, 0) }}</span>
        <div class="drop-down disable">
          <div class="drop-down-item">
            <img src="@/assets/svg/chart-icons/banker.svg" alt="Banker" />
            <img src="@/assets/svg/chart-icons/player.svg" alt="Player" />
            <span class="limit-text">{{ formatAmountWithKM(mainLimitAmountPerFight, 0) }}</span>
          </div>
          <div class="drop-down-item">
            <img class="draw-icon" src="@/assets/svg/chart-icons/draw.svg" alt="Draw" />
            <span class="limit-text">{{ formatAmountWithKM(drawLimitAmountPerFight, 0) }}</span>
          </div>
        </div>
      </div>

      <span class="desktop-header__separator" />

      <!--
        Text links, not the v1 icon buttons: the design has no icons here. Lobby
        is not in the design either, but it is the member's way back out of the
        game, so it stays when the platform supplies a URL.
      -->
      <button v-if="lobbyUrl" type="button" class="desktop-header__link" @click.stop="redirectToLobby()">
        {{ $t('Lobby') }}
      </button>
      <!-- No HISTORY link: the rail's CURRENT / TODAY already is the history. -->
      <!-- The class is a position hook too: the menu popup opens under it. -->
      <button
        type="button"
        class="desktop-header__link desktop-header__link--help"
        :class="{ active: activePopupPage === PopupPage.help }"
        @click.stop="openMenuPopup(PopupPage.help)"
      >
        {{ $t('Game help') }}
      </button>

      <!-- The shared dropdown, restyled from here into the design's pill. -->
      <div class="desktop-header__lang">
        <LanguageDropdown />
      </div>
    </nav>

    <!-- The announcement ticker (desktop-client owns it and its popovers). -->
    <div class="desktop-header__ticker">
      <slot name="ticker" />
    </div>

    <div class="desktop-header__user" :class="{ 'desktop-header__user--menu': withCommission }">
      <img class="desktop-header__avatar" src="@/assets/svg/profile-icon.svg" alt="" />
      <span class="desktop-header__name">{{ memberName }}</span>
      <div class="drop-down">
        <ToggleBtn
          v-if="config.availableOddsType.length > 1"
          class="drop-down-item"
          :leftTxt="$t('Eu')"
          :rightTxt="$t('My')"
          :value="toggleValue"
          @toggle="(e) => toggleOdds(e)"
        />
        <div v-if="withCommission" class="drop-down-item" @click="logoutCreditUser()">{{ $t('Logout') }}</div>
      </div>
    </div>

    <div class="desktop-header__wallet">
      <div class="desktop-header__balance" :class="{ 'tooltip-container': isLargeBalance }">
        {{ currencySymbol }}
        <animated-amount
          :value="balance"
          :formatValue="isLargeBalance ? formatLargeAmount : formatAmount"
          :duration="500"
        />
        <span v-if="isLargeBalance" class="desktop-header__balance-tooltip">
          {{ $t('Balance') }}: {{ currencySymbol }}{{ formatAmount(balance) }}
        </span>
      </div>
      <Transition name="fade">
        <div v-if="futureDiff" class="desktop-header__diff">+ {{ userFutureDiff }}</div>
      </Transition>
    </div>
  </header>
</template>

<script>
/*
 * The desktop v2 header (Figma node 457:6831).
 *
 * A separate component from `header.vue` rather than another set of flags on
 * it: the composition is different enough -- a left-aligned settings row of
 * plain text links instead of the v1 icon menu, a language pill, a different
 * profile mark -- that branching inside the shared header would put the mobile
 * and v1 desktop layouts at risk for no gain. The data and the actions are the
 * same ones `header.vue` uses, so behaviour is unchanged.
 */
import { mapGetters, mapActions } from 'vuex';
import { formatAmount, formatAmountWithKM, formatLargeAmount } from '@/utils/helpers';
import LanguageDropdown from '@/components/menus/language-dropdown';
import { PopupPage } from '@/utils/index.model';
import Logo from '@/components/logo.vue';
import AnimatedAmount from '@/components/animated-amount.vue';
import ToggleBtn from '@/components/toggle-btn.vue';
import { initMgConnection } from '@/utils/websocket';

export default {
  name: 'DesktopHeader',
  components: {
    LanguageDropdown,
    AnimatedAmount,
    Logo,
    ToggleBtn,
  },
  props: {
    // Only one arena is open, so the client is in single view regardless.
    singleOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      PopupPage,
      // Four files, not two: each glyph ships from Figma with its state colour
      // baked into the path fill, so the pair is recoloured rather than tinted.
      icons: {
        one: require('@/assets/svg/view-one-pane.svg'),
        oneActive: require('@/assets/svg/view-one-pane-active.svg'),
        two: require('@/assets/svg/view-two-pane-inactive.svg'),
        twoActive: require('@/assets/svg/view-two-pane.svg'),
      },
    };
  },
  computed: {
    ...mapGetters({
      balance: 'general/balance',
      futureDiff: 'general/futureDiff',
      activePopupPage: 'menu/activePopupPage',
      config: 'general/config',
      member: 'general/member',
      token: 'general/token',
      oddsType: 'general/oddsType',
      lobbyUrl: 'general/lobbyUrl',
      selectedArena: 'general/selectedArena',
      viewMode: 'taiXiu/viewMode',
    }),
    // With a single arena the client shows one pane whatever the preference.
    shownViewMode() {
      return this.singleOnly ? 1 : this.viewMode;
    },
    userFutureDiff() {
      return formatAmount(this.futureDiff, 2);
    },
    currencySymbol() {
      return this.member.currencySymbol;
    },
    memberName() {
      const name = this.member.displayName || '';
      return name.length > 14 ? `${name.substr(0, 14)}...` : name;
    },
    withCommission() {
      return this.config.withCommission;
    },
    toggleValue() {
      return this.oddsType === 'my';
    },
    isLargeBalance() {
      return Number(this.balance) > 99999999.99;
    },
    mainLimitAmountPerFight() {
      if (this.selectedArena && this.selectedArena.mainLimitAmountPerFight) {
        return Number(this.selectedArena.mainLimitAmountPerFight) < Number(this.config.mainLimitAmountPerFight)
          ? Number(this.selectedArena.mainLimitAmountPerFight)
          : Number(this.config.mainLimitAmountPerFight);
      }
      return Number(this.config.mainLimitAmountPerFight);
    },
    drawLimitAmountPerFight() {
      if (this.selectedArena && this.selectedArena.drawLimitAmountPerFight) {
        return Number(this.selectedArena.drawLimitAmountPerFight) < Number(this.config.drawLimitAmountPerFight)
          ? Number(this.selectedArena.drawLimitAmountPerFight)
          : Number(this.config.drawLimitAmountPerFight);
      }
      return Number(this.config.drawLimitAmountPerFight);
    },
  },
  methods: {
    ...mapActions('taiXiu', ['setViewMode']),
    ...mapActions('menu', ['updateMenu']),
    ...mapActions('general', ['updateOddsType']),
    formatAmount,
    formatLargeAmount,
    formatAmountWithKM,
    openMenuPopup(page) {
      this.updateMenu({ isPopupOpen: true, isJackpotPopupOpen: false, activePopupPage: page });
    },
    logoutCreditUser() {
      if (window.parent) {
        window.parent.postMessage('Logout', '*');
      }
    },
    toggleOdds(val) {
      const updatedOdds = this.config.availableOddsType[val ? 1 : 0];
      this.updateOddsType(updatedOdds);
      initMgConnection(this.token, updatedOdds);
    },
    redirectToLobby() {
      window.location.href = this.lobbyUrl;
    },
  },
};
</script>

<style lang="scss" scoped>
/*
 * v2.1 prototype: one 54px row, 20px gutters, 16px between groups -- logo (16
 * tall), settings, the announcement ticker flexing into the spare width, then
 * profile and wallet against the right gutter.
 */
.desktop-header {
  height: 54px;
  box-sizing: border-box;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  background-color: $ga-navy-900;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  white-space: nowrap;
  z-index: 50001;
}

.desktop-header__logo {
  width: auto;
  height: 16px;
  flex: 0 0 auto;
  object-fit: contain;
}

.desktop-header__settings {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: 6px;
  min-width: 0;
}

.desktop-header__label {
  color: #6a91a7;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.desktop-header__value {
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  white-space: nowrap;
  font-feature-settings: 'tnum' on, 'lnum' on;
}

.desktop-header__view {
  display: flex;
  align-items: center;
  gap: 8px;
}

.desktop-header__view-btn {
  width: 22px;
  height: 18px;
  padding: 0;
  border: 0;
  background: transparent;
  display: block;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  img {
    width: 22px;
    height: 18px;
    display: block;
  }

  &.active {
    cursor: default;
  }

  &:hover:not(.active) {
    opacity: 0.75;
  }
}

.desktop-header__max {
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;

  &:hover .drop-down {
    display: block;
  }
}

.desktop-header__separator {
  width: 1px;
  height: 22px;
  background-color: #1a263d;
}

.desktop-header__link {
  padding: 0;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.04em;
  font-family: inherit;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;

  &.active {
    color: #ceb57d;
  }

  &:hover {
    color: #efc87f;
  }
}

/*
 * The design wraps the language control in a 101x31 pill. The control itself is
 * the shared dropdown, so the pill is drawn here and its inner spacing is
 * flattened -- the shared component is left alone for mobile and v1.
 */
.desktop-header__lang {
  height: 30px;
  padding: 0 10px 0 7px;
  border: 1px solid #36505d;
  border-radius: 999px;
  background: transparent;
  box-sizing: border-box;
  display: flex;
  align-items: center;

  ::v-deep .language-settings {
    width: auto;
  }

  ::v-deep .dropdowns-container {
    width: auto;
  }

  ::v-deep .dropdowns__item__wrapper__content {
    padding: 0;

    &:hover {
      background-image: none;
    }
  }

  ::v-deep .dropdowns__item__wrapper__content__title > img {
    width: 16px;
    height: 16px;
    display: block;
  }

  ::v-deep .txt {
    min-width: 0;
    // 4px, measured: flag ends at 805 in the frame, the label starts at 809.
    margin-left: 7px;
    font-size: 12px;
    font-weight: 500;
  }

  ::v-deep ul {
    // The pill is 101px wide; the menu needs its own width, and it hangs from
    // the pill's bottom edge rather than the text's.
    top: calc(100% + 10px);
    left: -12px;
  }
}

.desktop-header__ticker {
  flex: 1 1 0;
  min-width: 0;
  // Spaced by the header's own 16px gap.
  margin: 0;
}

.desktop-header__user {
  display: flex;
  align-items: center;
  position: relative;
  // Content height, not the header's: `height: 100%` pushed the dropdown down
  // to the foot of the header, where it landed on the ticker instead of
  // hanging from the name the way the MAX one hangs from its value.
  height: auto;
  // 1094 in the frame: everything to the left is laid out from the logo, and
  // this block plus the wallet sit against the right gutter.
  margin-left: auto;
  gap: 8px;

  &--menu {
    cursor: pointer;
  }

  &:hover .drop-down {
    display: block;
  }
}

.desktop-header__avatar {
  width: 20px;
  height: 20px;
  display: block;
}

.desktop-header__name {
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.desktop-header__wallet {
  position: relative;
  display: flex;
  align-items: center;
}

.desktop-header__balance {
  color: #efc87f;
  font-size: 15px;
  font-weight: 700;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  font-feature-settings: 'tnum' on, 'lnum' on;

  &.tooltip-container {
    cursor: pointer;

    &:hover .desktop-header__balance-tooltip {
      opacity: 1;
    }
  }
}

.desktop-header__balance-tooltip {
  position: absolute;
  top: 120%;
  right: 0;
  z-index: 1;
  padding: 10px;
  border-radius: 6px;
  background: #102741;
  color: #fcc751;
  line-height: 1.5;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
}

.desktop-header__diff {
  position: absolute;
  top: 100%;
  right: 0;
  font-size: 16px;
  font-weight: 600;
  color: #ffefcc;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 2s ease;
}

.fade-enter-from {
  opacity: 0;
  top: 100%;
}

.fade-leave-to {
  opacity: 0;
  top: 0;
}

@include dropDown;

/*
 * Both header dropdowns inherit their text colour, which came out black on the
 * dark panel.
 */
.desktop-header__max .drop-down,
.desktop-header__user .drop-down {
  color: #fff;
}
</style>
